import type { ReactNode } from "react"
import type { LucideIcon } from "lucide-react"

/**
 * An empty list or a search with no result: an icon in a neutral circle, an
 * optional title, one sentence and an optional action. The compact variant
 * fits inside a column or a card slot. For failures, use `tone="error"` so
 * the icon reads as a problem, not as "nothing here yet".
 */
export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  compact = false,
  tone = "neutral",
  iconClassName,
  circleClassName,
  className = "",
}: {
  icon: LucideIcon
  title?: string
  description: ReactNode
  action?: ReactNode
  compact?: boolean
  tone?: "neutral" | "error"
  /** Overrides the icon color (e.g. a stage color). */
  iconClassName?: string
  /** Overrides the circle behind the icon (e.g. white on a tinted column). */
  circleClassName?: string
  className?: string
}) {
  return (
    <div
      className={`flex flex-col items-center justify-center text-center ${
        compact ? "gap-2 px-3 py-5" : "gap-3 px-5 py-8"
      } ${className}`}
    >
      <span
        aria-hidden
        className={`flex items-center justify-center rounded-full ${
          compact ? "size-9" : "size-11"
        } ${
          circleClassName ??
          (tone === "error" ? "bg-feedback-error-bg" : "bg-neutral-100")
        }`}
      >
        <Icon
          className={`${compact ? "size-4" : "size-5"} ${
            iconClassName ??
            (tone === "error" ? "text-feedback-error-text" : "text-ink-muted")
          }`}
        />
      </span>
      {title ? (
        <p className="text-sm font-semibold text-ink-headline">{title}</p>
      ) : null}
      <p className="max-w-xs text-sm leading-relaxed text-ink-muted">
        {description}
      </p>
      {action}
    </div>
  )
}
